"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyRetailerSpecificDiscounts = exports.mergeDiscountedWholesalerIntoWholesaler = exports.mergeDiscountedProductIntoProduct = exports.sanitizeDiscounts = exports.buildProductsDiscountSummary = exports.buildEmptyDiscountedProduct = void 0;
const error_1 = require("@creoate/core-lib/error");
const math_utils_1 = require("@creoate/core-lib/utils/math-utils");
const _shared_1 = require("../shared");
// #region builders
const buildEmptyDiscountedProduct = (productId) => ({
    productId,
    lineItemSubtotal: 0,
    subtotal: 0,
    total: 0,
    discounts: {
        direct: {
            subtotal: 0,
            discountAmount: 0,
            total: 0,
        },
        bulk: {
            subtotal: 0,
            discountAmount: 0,
            total: 0,
        },
        store: {
            subtotal: 0,
            discountAmount: 0,
            total: 0,
        },
    },
});
exports.buildEmptyDiscountedProduct = buildEmptyDiscountedProduct;
const buildProductsDiscountSummary = (acc, discountedProduct) => ({
    products: [...acc.products, discountedProduct],
    lineItemSubtotal: acc.lineItemSubtotal + discountedProduct.lineItemSubtotal,
    subtotal: acc.subtotal + discountedProduct.subtotal,
    total: acc.total + discountedProduct.total,
    discounts: {
        direct: acc.discounts.direct + discountedProduct.discounts.direct.discountAmount,
        bulk: acc.discounts.bulk + discountedProduct.discounts.bulk.discountAmount,
        store: acc.discounts.store + discountedProduct.discounts.store.discountAmount,
    }
});
exports.buildProductsDiscountSummary = buildProductsDiscountSummary;
// #endregion builders
// #region sanitizers
/**
 * Sanitizes the discounts object by rounding the discount values to 0 decimal places.
 *
 * @param discounts - The discounts object to be sanitized.
 * @returns The sanitized discounts object.
 */
const sanitizeDiscounts = (discounts) => (Object.assign(Object.assign(Object.assign(Object.assign({}, (discounts.direct && { direct: (0, math_utils_1.roundToDecimalPlaces)(discounts.direct, 0) })), { bulk: (0, math_utils_1.roundToDecimalPlaces)(discounts.bulk, 0), store: (0, math_utils_1.roundToDecimalPlaces)(discounts.store, 0) }), (discounts.campaign && { campaign: (0, math_utils_1.roundToDecimalPlaces)(discounts.campaign, 0) })), (discounts.voucher && { voucher: (0, math_utils_1.roundToDecimalPlaces)(discounts.voucher, 0) })));
exports.sanitizeDiscounts = sanitizeDiscounts;
// #endregion sanitizers
// #region discounts
const mergeDiscountedProductIntoProduct = (product, discountedProd) => {
    var _a;
    const discounts = discountedProd.discounts;
    return Object.assign(Object.assign({}, product), { 
        // This is the first time when product order summary is created
        productOrderSummary: {
            lineItemSubtotal: discountedProd.lineItemSubtotal,
            subtotal: discountedProd.subtotal,
            total: discountedProd.total,
            discounts: {
                direct: discounts.direct.discountAmount,
                bulk: discounts.bulk.discountAmount,
                store: ((_a = discounts.store) === null || _a === void 0 ? void 0 : _a.discountAmount) || 0,
            },
            taxes: {
                vat: 0, // initial value for vat
            },
        } });
};
exports.mergeDiscountedProductIntoProduct = mergeDiscountedProductIntoProduct;
const mergeDiscountedWholesalerIntoWholesaler = (wholesaler, discountedWholesaler) => (Object.assign(Object.assign({}, wholesaler), { products: wholesaler.products.map((product) => {
        const discountedProd = discountedWholesaler.products.find((discountProd) => discountProd.productId === product.productId || discountProd.productId === product.productWpId);
        // this should never happen
        if (!discountedProd) {
            throw new error_1.BusinessError(_shared_1.ERRORS.VALIDATION_ERROR, `Discounted product for ${product.productId || product.productWpId} was not found`);
        }
        return (0, exports.mergeDiscountedProductIntoProduct)(product, discountedProd);
    }), wholesalerOrderSummary: {
        lineItemSubtotal: discountedWholesaler.lineItemSubtotal,
        subtotal: discountedWholesaler.subtotal,
        total: discountedWholesaler.total,
        discounts: Object.assign(Object.assign({}, (discountedWholesaler.discounts.direct && { direct: discountedWholesaler.discounts.direct })), { bulk: discountedWholesaler.discounts.bulk, store: discountedWholesaler.discounts.store }),
        taxes: {
            borderFee: 0,
            borderFeePercent: 0,
            shippingVat: 0,
            vat: 0,
            vatPercent: 0,
        },
        shipping: 0,
    } }));
exports.mergeDiscountedWholesalerIntoWholesaler = mergeDiscountedWholesalerIntoWholesaler;
const applyRetailerSpecificDiscounts = (wholesaler, retailerSpecificDiscounts) => {
    if (!retailerSpecificDiscounts || retailerSpecificDiscounts.length === 0) {
        return wholesaler;
    }
    const applicableDiscount = retailerSpecificDiscounts.find((discount) => discount.wholesalerId === wholesaler.wholesalerWpId);
    return Object.assign(Object.assign({}, wholesaler), { storeDiscount: applicableDiscount
            ? [
                {
                    storeDiscountPercentage: applicableDiscount.discountPercentage,
                    storeDiscountMinOrderAmount: applicableDiscount.minOrderAmount,
                },
            ]
            : wholesaler.storeDiscount });
};
exports.applyRetailerSpecificDiscounts = applyRetailerSpecificDiscounts;
// #endregion discounts
